import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpDown, LogOut, RefreshCw, Search, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { adminHeaders, api, formatApiError } from "../api";
import { leadStatuses } from "../content";

const statusDot = {
  Novo: "bg-[#00B8D4]",
  "Em contato": "bg-amber-400",
  Fechado: "bg-emerald-500",
  Arquivado: "bg-neutral-400",
};

const formatDate = (iso) =>
  new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

const StatCard = ({ label, value, testId }) => (
  <div className="rounded-3xl border border-black/10 bg-white p-6" data-testid={testId}>
    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-black/45">{label}</p>
    <p className="mt-3 font-display text-4xl font-extrabold tracking-tighter text-[#1A1A1A]">{value ?? "—"}</p>
  </div>
);

const StatusSelect = ({ lead, onChange, testId }) => (
  <span className="inline-flex items-center gap-2">
    <span className={`h-2 w-2 rounded-full ${statusDot[lead.status] || "bg-neutral-400"}`} />
    <select
      value={lead.status}
      data-testid={testId}
      aria-label={`Status do contato ${lead.name}`}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => onChange(lead.id, e.target.value)}
      className="cursor-pointer rounded-lg border border-black/10 bg-white px-2 py-1 text-xs text-[#1A1A1A] outline-none transition-colors focus:border-[#1D4E89]"
    >
      {leadStatuses.map((s) => (
        <option key={s} value={s}>
          {s}
        </option>
      ))}
    </select>
  </span>
);

export default function AdminLeads() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sort, setSort] = useState("desc");
  const [selected, setSelected] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const params = { sort };
        if (statusFilter) params.status = statusFilter;
        if (search.trim()) params.search = search.trim();
        const [leadsRes, statsRes] = await Promise.all([
          api.get("/admin/leads", { headers: adminHeaders(), params }),
          api.get("/admin/stats", { headers: adminHeaders() }),
        ]);
        setLeads(leadsRes.data);
        setStats(statsRes.data);
        setError("");
      } catch (err) {
        if (err.response?.status === 401) {
          sessionStorage.removeItem("lr_admin_token");
          navigate("/admin/login", { replace: true });
        } else {
          setError("Não foi possível carregar os contatos.");
        }
      } finally {
        setLoading(false);
      }
    },
    [navigate, search, sort, statusFilter]
  );

  useEffect(() => {
    if (!sessionStorage.getItem("lr_admin_token")) {
      navigate("/admin/login", { replace: true });
      return;
    }
    load();
    const interval = setInterval(() => load(true), 15000);
    return () => clearInterval(interval);
  }, [load, navigate]);

  useEffect(() => {
    const t = setTimeout(() => load(), 300);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/leads/${id}`, { status }, { headers: adminHeaders() });
      setLeads((ls) => ls.map((l) => (l.id === id ? data : l)));
      setSelected((s) => (s && s.id === id ? data : s));
      toast.success(`Status atualizado para “${status}”.`);
      const statsRes = await api.get("/admin/stats", { headers: adminHeaders() });
      setStats(statsRes.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Não foi possível atualizar o status."));
    }
  };

  const deleteDemoData = async () => {
    if (!window.confirm("Apagar todos os contatos de demonstração? Esta ação não pode ser desfeita.")) return;
    setDeleting(true);
    try {
      const { data } = await api.delete("/admin/leads", { headers: adminHeaders() });
      toast.success(`${data.deleted} contato(s) de demonstração apagados.`);
      setSelected(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Não foi possível apagar os dados."));
    } finally {
      setDeleting(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem("lr_admin_token");
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F7F7F5] font-body text-[#1A1A1A]" data-testid="admin-leads-page">
      <header className="sticky top-0 z-30 border-b border-black/10 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3 px-6 py-4">
          <span className="grid h-9 w-9 place-items-center rounded-full bg-black font-display text-xs font-extrabold text-white">LR</span>
          <div className="leading-tight">
            <p className="font-display text-sm font-bold tracking-tight">Painel de contatos</p>
            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-black/45">Demonstração · dados expiram em 24h</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              data-testid="admin-refresh-button"
              onClick={() => load()}
              aria-label="Atualizar lista de contatos"
              className="grid h-9 w-9 place-items-center rounded-full border border-black/15 text-black/60 transition-colors hover:border-[#1D4E89] hover:text-[#1D4E89]"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              type="button"
              data-testid="admin-delete-demo-button"
              onClick={deleteDemoData}
              disabled={deleting}
              className="flex items-center gap-2 rounded-full border border-red-200 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.14em] text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 size={13} />
              {deleting ? "Apagando…" : "Apagar dados de demonstração"}
            </button>
            <button
              type="button"
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 rounded-full bg-black px-4 py-2 font-mono text-[10px] uppercase tracking-[0.14em] text-white transition-colors hover:bg-[#1D4E89]"
            >
              <LogOut size={13} />
              Sair
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard label="Total de contatos" value={stats?.total} testId="stat-total" />
          <StatCard label="Novos contatos" value={stats?.novos} testId="stat-novos" />
          <StatCard label="Contatos em andamento" value={stats?.em_contato} testId="stat-em-contato" />
          <StatCard label="Projetos fechados" value={stats?.fechados} testId="stat-fechados" />
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <label className="relative flex-1 min-w-[220px]">
            <span className="sr-only">Buscar por nome, empresa ou e-mail</span>
            <Search size={14} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-black/35" />
            <input
              type="search"
              data-testid="leads-search"
              placeholder="Buscar por nome, empresa ou e-mail"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-full border border-black/15 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition-colors focus:border-[#1D4E89]"
            />
          </label>
          <label className="flex items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-black/45">Status</span>
            <select
              data-testid="leads-filter-status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-full border border-black/15 bg-white px-4 py-2.5 text-sm outline-none transition-colors focus:border-[#1D4E89]"
            >
              <option value="">Todos</option>
              {leadStatuses.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            data-testid="leads-sort-date"
            onClick={() => setSort((s) => (s === "desc" ? "asc" : "desc"))}
            className="flex items-center gap-2 rounded-full border border-black/15 bg-white px-4 py-2.5 text-sm text-black/70 transition-colors hover:border-[#1D4E89] hover:text-[#1D4E89]"
          >
            <ArrowUpDown size={14} />
            Data {sort === "desc" ? "↓" : "↑"}
          </button>
        </div>

        {error && (
          <p data-testid="leads-error" role="alert" className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </p>
        )}

        <div className="mt-6 overflow-x-auto rounded-3xl border border-black/10 bg-white" data-testid="leads-table-wrapper">
          <table className="w-full min-w-[960px] text-left text-sm" data-testid="leads-table">
            <thead>
              <tr className="border-b border-black/10 font-mono text-[10px] uppercase tracking-[0.16em] text-black/45">
                <th className="px-5 py-4 font-medium">Ref.</th>
                <th className="px-5 py-4 font-medium">Data</th>
                <th className="px-5 py-4 font-medium">Nome</th>
                <th className="px-5 py-4 font-medium">Empresa</th>
                <th className="px-5 py-4 font-medium">E-mail</th>
                <th className="px-5 py-4 font-medium">Tipo</th>
                <th className="px-5 py-4 font-medium">Investimento</th>
                <th className="px-5 py-4 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr
                  key={lead.id}
                  data-testid={`lead-row-${lead.reference_number}`}
                  onClick={() => setSelected(lead)}
                  className="cursor-pointer border-b border-black/5 transition-colors last:border-0 hover:bg-black/[0.02]"
                >
                  <td className="px-5 py-4 font-mono text-xs text-[#1D4E89]">{lead.reference_number}</td>
                  <td className="whitespace-nowrap px-5 py-4 text-xs text-black/55">{formatDate(lead.created_at)}</td>
                  <td className="px-5 py-4 font-medium">{lead.name}</td>
                  <td className="px-5 py-4 text-black/70">{lead.company}</td>
                  <td className="px-5 py-4 text-black/70">{lead.email}</td>
                  <td className="px-5 py-4 text-black/70">{lead.project_type}</td>
                  <td className="whitespace-nowrap px-5 py-4 text-black/70">{lead.budget_range}</td>
                  <td className="px-5 py-4">
                    <StatusSelect lead={lead} onChange={updateStatus} testId={`lead-status-${lead.reference_number}`} />
                  </td>
                </tr>
              ))}
              {!loading && leads.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-5 py-14 text-center text-sm text-black/45" data-testid="leads-empty">
                    Nenhum contato de demonstração no momento.
                  </td>
                </tr>
              )}
              {loading && leads.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-5 py-14 text-center text-sm text-black/45">
                    Carregando…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </main>

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4 backdrop-blur-sm"
            onClick={() => setSelected(null)}
            data-testid="lead-detail-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 24 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label={`Detalhes do contato ${selected.name}`}
              className="w-full max-w-lg rounded-[2rem] border border-black/10 bg-white p-8 shadow-2xl"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-mono text-xs tracking-[0.1em] text-[#1D4E89]" data-testid="lead-detail-ref">
                    {selected.reference_number}
                  </p>
                  <h2 className="mt-2 font-display text-2xl font-extrabold tracking-tighter">{selected.name}</h2>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.16em] text-black/45">
                    {formatDate(selected.created_at)}
                  </p>
                </div>
                <button
                  type="button"
                  data-testid="lead-detail-close"
                  onClick={() => setSelected(null)}
                  aria-label="Fechar detalhes"
                  className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-black/15 text-black/60 transition-colors hover:border-black/40"
                >
                  <X size={15} />
                </button>
              </div>

              <dl className="mt-6 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="font-mono text-[9px] uppercase tracking-[0.18em] text-black/45">Empresa</dt>
                  <dd className="mt-1" data-testid="lead-detail-company">{selected.company}</dd>
                </div>
                <div>
                  <dt className="font-mono text-[9px] uppercase tracking-[0.18em] text-black/45">E-mail</dt>
                  <dd className="mt-1 break-all" data-testid="lead-detail-email">{selected.email}</dd>
                </div>
                <div>
                  <dt className="font-mono text-[9px] uppercase tracking-[0.18em] text-black/45">Tipo de projeto</dt>
                  <dd className="mt-1">{selected.project_type}</dd>
                </div>
                <div>
                  <dt className="font-mono text-[9px] uppercase tracking-[0.18em] text-black/45">Investimento</dt>
                  <dd className="mt-1">{selected.budget_range}</dd>
                </div>
              </dl>

              <div className="mt-6">
                <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-black/45">Mensagem</p>
                <p className="mt-2 rounded-xl bg-[#F7F7F5] p-4 text-sm leading-relaxed text-black/75" data-testid="lead-detail-message">
                  {selected.message}
                </p>
              </div>

              <div className="mt-6 flex items-center justify-between gap-4">
                <StatusSelect lead={selected} onChange={updateStatus} testId="lead-detail-status" />
                <span className="rounded-full border border-[#1D4E89]/25 bg-[#1D4E89]/5 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.14em] text-[#1D4E89]">
                  Expira em 24h
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
